import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";
import { Button, Typography } from "@iluvatar/ui-kit";

import {
  DefaultError,
  InfiniteData,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { MATCH_COLLECTION } from "@iluvatar/global/src/constants";
import { MatchRecord } from "@iluvatar/global/src/typings";
import { Time } from "@iluvatar/global/src/utilities/time";

import { ErrorBoBoMessage } from "@/components/ErrorBoBoMessage";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { QueryMatchUser } from "@/components/MatchUserPreview";
import { MessageContainer } from "@/components/MessageContainer";
import { MessageSearch } from "@/components/MessageSearch";
import { SubscribedFilters } from "@/components/SubscribedFilters";
import { useAuthenticationContext } from "@/contexts/authentication/authentication.context";
import { useNotificationDismissal } from "@/hooks/notificationDismissal";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMatchedProfiles } from "@/hooks/useMatchedUser";
import { useNotifications } from "@/hooks/useNotifications";
import { useSubscribedFilters } from "@/hooks/useSubscribedFilters";
import DefaultLayout from "@/layout/default-page";
import { cn } from "@/utilities";
const queryLimit = 25;

function NotifiedMatchUser(props: React.ComponentProps<typeof QueryMatchUser>) {
  const { hasUnread } = useNotifications("messages", props.matchId);
  return (
    <QueryMatchUser
      {...props}
      className={cn(props.className, {
        "border-amber-500/50 hover:border-amber-500": hasUnread,
      })}
    />
  );
}

type TData = MatchRecord & { _id: string; _ref: string };
type PageParams = { updatedAt: number; id: string } | null;

const MatchesPage: React.FC = () => {
  const { uid } = useAuthenticationContext();
  const { user, api } = useCurrentUser();
  const hasSubscriptionAccess = useSubscriptionAccess(user?.isSubscribed);
  const communityId = "default";
  const [disconnected, setDisconnected] = useState<string[]>([]);
  const hasMatches = Boolean(user?.matchMap[communityId]?.length);
  const { search = "" } = useSearch({ from: "/matches" });
  const navigate = useNavigate({ from: "/matches" });
  const term = search.trim().toLocaleLowerCase();
  const { filters, settings, setSettings } = useSubscribedFilters(
    "matches",
    user?.uid,
  );
  const activeFilters = hasSubscriptionAccess ? filters : {};
  const isFiltering = Boolean(term) || Object.keys(activeFilters).length > 0;
  const {
    data: profiles,
    isFetching: isFetchingProfiles,
    error: profileError,
  } = useMatchedProfiles(
    communityId,
    user?.matchMap[communityId] ?? [],
    isFiltering,
    activeFilters,
  );

  const { data, fetchNextPage, hasNextPage, refetch, isFetching, error } =
    useInfiniteQuery<
      TData[],
      DefaultError,
      InfiniteData<TData[], PageParams>,
      readonly unknown[],
      PageParams
    >({
      queryKey: ["match-query", communityId, uid],
      initialPageParam: null,
      staleTime: Time.mins,
      networkMode: "offlineFirst",
      async queryFn({ pageParam }) {
        const { sdk, firestore } = await matchQueryRuntime();
        const {
          getDocs,
          query,
          collection,
          and,
          or,
          where,
          orderBy,
          documentId,
          startAfter,
          limit,
        } = sdk;
        const dd = await getDocs(
          query(
            collection(firestore, MATCH_COLLECTION),
            and(
              or(
                where("sourceUID", "==", uid || ""),
                where("targetUID", "==", uid || ""),
              ),
              where("communityId", "==", communityId),
              where("inactive", "==", false),
            ),
            orderBy("updatedAt", "asc"),
            orderBy(documentId(), "asc"),
            ...(pageParam
              ? [startAfter(pageParam.updatedAt, pageParam.id)]
              : []),
            limit(queryLimit),
          ),
        );

        return dd.docs.map((x) => ({
          ...x.data(),
          _id: x.id,
          _ref: x.ref.path,
        })) as TData[];
      },
      refetchOnMount: (q) => {
        return Boolean(q.isStale());
      },
      getNextPageParam: (lastPage) => {
        if (
          lastPage?.length === queryLimit &&
          typeof lastPage[lastPage.length - 1].updatedAt === "number"
        ) {
          return {
            updatedAt: lastPage[lastPage.length - 1].updatedAt!,
            id: lastPage[lastPage.length - 1]._id,
          };
        }
        return;
      },
      enabled: Boolean(uid),
    });

  const previousMatches = useRef<string>();
  const matchMap = user?.matchMap[communityId];
  const accountUid = user?.uid;
  useEffect(() => {
    if (!accountUid || accountUid !== uid) {
      previousMatches.current = undefined;
      return;
    }
    const current = JSON.stringify([uid, matchMap]);
    if (previousMatches.current && previousMatches.current !== current) {
      void refetch();
    }
    previousMatches.current = current;
  }, [uid, accountUid, matchMap, refetch]);

  useNotificationDismissal("matches", undefined, () => {
    refetch();
  });

  const matchingUsers = new Set(
    profiles
      ?.filter(
        (profile) =>
          !term ||
          (profile.profile.displayName || profile.displayName || "Anonymous")
            .toLocaleLowerCase()
            .includes(term),
      )
      .map((profile) => profile.uid),
  );
  const matches = data?.pages?.reduce<TData[]>((_matches, p) => {
    return _matches.concat(
      (p || []).filter(
        (d) =>
          !disconnected.includes(d._id) &&
          (!isFiltering ||
            matchingUsers.has(d.sourceUID === uid ? d.targetUID : d.sourceUID)),
      ),
    );
  }, []);
  useEffect(() => {
    if (
      isFiltering &&
      profiles &&
      matchingUsers.size > 0 &&
      (matches?.length ?? 0) < queryLimit &&
      hasNextPage &&
      !isFetching &&
      !error
    ) {
      void fetchNextPage();
    }
  }, [
    isFiltering,
    profiles,
    matchingUsers.size,
    matches?.length,
    data?.pages.length,
    hasNextPage,
    isFetching,
    error,
    fetchNextPage,
  ]);
  const isSearching =
    isFiltering &&
    (isFetchingProfiles ||
      (matchingUsers.size > 0 &&
        hasNextPage &&
        (matches?.length ?? 0) < queryLimit));

  return (
    <DefaultLayout
      className={
        "profile-container ml-auto mr-auto [max-width:900px] flex flex-col [background:linear-gradient(180deg,_transparent_0%,_transparent_50%,_var(--color-background-default)_50%,_var(--color-background-default))] [@media(min-width:_900px)]:[--page-content-padding:42px] [@media(min-width:_900px)]:[padding-left:42px] [@media(min-width:_900px)]:[padding-right:42px] [&.profile-container--input]:[max-width:900px] [&.profile-container--input]:w-full [@media(max-width:_400px)]:[&.main-container]:[--page-content-padding:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-left:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-right:8px] flex start-start pb-12"
      }
      style={{
        height: hasMatches ? "" : "calc(100vh - var(--nav-height) - 1px)",
      }}
    >
      <div className="flex items-center justify-between gap-4 mt-8 mb-4 w-full">
        <Typography variant="h2" className="shrink-0">
          Messages
        </Typography>
        <div
          className={cn(
            "flex min-w-0 items-center gap-1",
            !hasMatches && "invisible",
          )}
        >
          <MessageSearch
            value={search}
            onChange={(value) => {
              void navigate({
                search: (previous) => ({
                  ...previous,
                  search: value || undefined,
                }),
                replace: true,
                resetScroll: false,
              });
            }}
          />
          <SubscribedFilters
            isSubscribed={hasSubscriptionAccess}
            key={user?.uid}
            settings={settings}
            onChange={setSettings}
          />
        </div>
      </div>

      <ErrorBoBoMessage error={error || profileError} />
      {isSearching && !error && !profileError && (
        <LoadingIndicator aria-label="Searching messages" />
      )}
      {isFiltering &&
        profiles &&
        data &&
        !matches?.length &&
        !isSearching &&
        !error &&
        !profileError && (
          <Typography className="my-8 text-center w-full">
            No conversations found.
          </Typography>
        )}

      {user?.matchMap[communityId] && !user.matchMap[communityId].length && (
        <MessageContainer>
          <Typography variant="h2">No Matches yet</Typography>
        </MessageContainer>
      )}

      {matches?.map((match) => {
        return (
          <Link
            key={match._id}
            to="/chat/$chatId"
            params={{ chatId: match._id }}
          >
            <NotifiedMatchUser
              key={match._id}
              matchId={match._id}
              communityId={match.communityId}
              onDisconnect={async () => {
                if (user?.uid) {
                  const matchUserId =
                    match.sourceUID == user.uid
                      ? match.targetUID
                      : match.sourceUID;
                  await api.disconnectMatch(user.uid, matchUserId, match._id);
                  setDisconnected((dd) => [...dd, match._id]);
                  refetch();
                }
              }}
              uid={match.sourceUID == uid ? match.targetUID : match.sourceUID}
              className={cn(
                "border border-[var(--color-primary--active)] px-4 py-1 rounded-[16px] hover:border-[var(--color-primary)] clickable cursor-pointer mb-2",
              )}
            />
          </Link>
        );
      })}

      {hasNextPage && (!isFiltering || matchingUsers.size > 0) && (
        <Button
          variant="outline"
          disabled={isFetching}
          onClick={() => fetchNextPage()}
          className="mt-5"
        >
          Load More
        </Button>
      )}
    </DefaultLayout>
  );
};

async function matchQueryRuntime() {
  if (navigator.onLine) {
    const [sdk, { readFirestore }] = await Promise.all([
      import("firebase/firestore/lite"),
      import("@/services/firebase.read"),
    ]);
    return { sdk, firestore: readFirestore };
  }
  const [sdk, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("@/services/firebase.init"),
  ]);
  // The query APIs match. Keep each SDK paired with its own database instance.
  return {
    sdk: sdk as unknown as typeof import("firebase/firestore/lite"),
    firestore:
      firestore as unknown as import("firebase/firestore/lite").Firestore,
  };
}

export const Route = createFileRoute("/matches")({
  component: MatchesPage,
  validateSearch: (params: Record<string, unknown>): { search?: string } => ({
    search: typeof params.search === "string" ? params.search : undefined,
  }),
});
